import { format } from 'date-fns'
import { SUBSCRIPTIONS } from '@/constants/memberships'
import {
  ESG,
  FORECASTS,
  GIGAFACTORY_ASSESSMENT,
  LITHIUM_ION_BATTERIES,
  MARKET_ASSESSMENTS,
  PRECURSOR_ASSESSMENT,
  PRICE_ASSESSMENTS,
  SOURCE,
  SUBSCRIPTION_BRIEFING_COPPER,
  SUBSCRIPTION_FORECASTS_COPPER,
  SUBSCRIPTION_SERVICE_COPPER,
} from '~/constants/services/constants'

/**
 * Type for raw subscription data received from API
 */
export type TSubscription = {
  name: string
  slug: string
  status: string
  expiration_date: string
}

/**
 * Type for formatted subscription data
 */
export type TFormattedSubscription = {
  name: string
  slug: string
  status: string
  expirationDate: string
}

/**
 * Type for membership status data
 */
export type TMembershipStatus = TSubscription

/**
 * Type for products and services mapping
 */
type TProductsAndServices = {
  [key: string]: TFormattedSubscription[]
}

/**
 * Type for the final response structure
 */
export type TProductsResponse = {
  [key: string]: TFormattedSubscription[] | undefined
}

export type TSubscriptionResponse = TSubscription[]

/**
 * Mapping of membership names to their display names
 */
const membershipNames: Record<string, string> = {
  Gigafactory: GIGAFACTORY_ASSESSMENT.menu,
  'Battery Cell': LITHIUM_ION_BATTERIES.menu,
  'Battery Database': LITHIUM_ION_BATTERIES.menu,
  'Battery and Gigafactory': LITHIUM_ION_BATTERIES.menu,
  'Battery & Gigafactory': LITHIUM_ION_BATTERIES.menu,
  Precursor: PRECURSOR_ASSESSMENT.menu,
}

/**
 * Gets the display name for a membership
 * @param name - The original membership name
 * @returns The display name if found, otherwise the original name
 */
const getMembershipName = (name: string): string => {
  const displayName: string = membershipNames[name]
  return displayName || name
}

/**
 * Formats a subscription object to a standardised format
 * @param subscription - The subscription object to format
 * @returns A formatted subscription object
 */
function formatSubscription(
  subscription: TSubscription,
): TFormattedSubscription {
  const { name, slug, status } = subscription
  let expirationDate: string

  if (status === 'active' && subscription.expiration_date === 'none') {
    expirationDate = 'Never'
  } else if (subscription.expiration_date !== 'none') {
    expirationDate = format(
      new Date(subscription.expiration_date),
      'dd/MM/yyyy',
    )
  } else {
    expirationDate = subscription.expiration_date
  }

  return {
    name,
    slug,
    status,
    expirationDate,
  }
}

/**
 * Adapts membership statuses to a structured format organised by product
 * @param membershipStatuses - Array of membership status objects
 * @returns Structured object with memberships organised by product
 */
export function getAdaptedMembershipStatuses(
  membershipStatuses: TMembershipStatus[],
): TProductsResponse {
  const pricingName = 'Pricing'
  const forecastName = 'Forecast'
  const LiB = 'Lithium ion Batteries'
  const productsAndServices: TProductsAndServices = {}
  const productNames: Record<string, string> = {
    [pricingName]: PRICE_ASSESSMENTS.name,
    [forecastName]: FORECASTS.name,
    'Market-Assessment': MARKET_ASSESSMENTS.name,
    ESG: ESG.name,
  }

  const processedSubscriptions = processSubscriptions(membershipStatuses)
  const hasServiceCopperActive = membershipStatuses.some(
    (membership) =>
      membership.slug === SUBSCRIPTION_SERVICE_COPPER &&
      membership.status === 'active',
  )
  processedSubscriptions.forEach((membership) => {
    const { productCategory, updatedMembership } = categorizeSubscription(
      membership,
      pricingName,
      forecastName,
      LiB,
      hasServiceCopperActive,
    )

    const mappedCategory = productNames[productCategory] || productCategory

    if (!productsAndServices[mappedCategory]) {
      productsAndServices[mappedCategory] = []
    }

    const formattedSubscription = formatSubscription(updatedMembership)
    const existingSubscription = productsAndServices[mappedCategory].find(
      (sub) => sub.slug === formattedSubscription.slug,
    )

    if (!existingSubscription) {
      productsAndServices[mappedCategory].push(formattedSubscription)
    }
  })

  const mergedProductsAndServices =
    mergeProductsAndServices(productsAndServices)
  return createProductsResponse(mergedProductsAndServices)
}

/**
 * Processes subscriptions, handling copper subscriptions specially
 * @param membershipStatuses - Array of membership status objects
 * @returns Processed array of subscriptions
 */
function processSubscriptions(
  membershipStatuses: TMembershipStatus[],
): TMembershipStatus[] {
  const copperSubs = processCopperSubscriptions(
    membershipStatuses.filter((membership) =>
      membership.slug.includes('copper'),
    ),
  )

  const nonCopperSubs = membershipStatuses.filter(
    (membership) => !membership.slug.includes('copper'),
  )

  return [...copperSubs, ...nonCopperSubs]
}

/**
 * Processes copper subscriptions with special handling
 * @param copperSubscriptions - Array of copper-related membership status objects
 * @returns Processed array of copper subscriptions
 */
function processCopperSubscriptions(
  copperSubscriptions: TMembershipStatus[],
): TMembershipStatus[] {
  const serviceCopper = copperSubscriptions.find(
    (membership) => membership.slug === SUBSCRIPTION_SERVICE_COPPER,
  )

  if (serviceCopper?.status === 'active') {
    return [
      {
        ...serviceCopper,
        slug: SUBSCRIPTION_FORECASTS_COPPER,
        name: 'Copper',
      },
      {
        ...serviceCopper,
        slug: SUBSCRIPTION_BRIEFING_COPPER,
        name: 'Copper Briefing',
      },
    ]
  }

  return copperSubscriptions.filter(
    (membership) => membership.slug !== SUBSCRIPTION_SERVICE_COPPER,
  )
}

/**
 * Categorizes a subscription and updates its name
 * @param membership - The membership to categorize
 * @param pricingName - The pricing category name
 * @param forecastName - The forecast category name
 * @param LiB - The Lithium ion Batteries name
 * @param hasServiceCopperActive
 * @returns Object with product category and updated membership
 */
function categorizeSubscription(
  membership: TMembershipStatus,
  pricingName: string,
  forecastName: string,
  LiB: string,
  hasServiceCopperActive: boolean = false,
): { productCategory: string; updatedMembership: TMembershipStatus } {
  const membershipCopy = { ...membership }
  let productCategory: string

  const nameSegments = membershipCopy.name.split(': ')
  const isCopper = membershipCopy.slug.includes('copper')

  if (nameSegments.length > 1 && !isCopper) {
    productCategory = nameSegments[0]
    membershipCopy.name = nameSegments[1]
  } else if (isCopper) {
    productCategory = forecastName
    if (
      membershipCopy.slug === SUBSCRIPTION_FORECASTS_COPPER &&
      !hasServiceCopperActive
    ) {
      membershipCopy.name = nameSegments[1]
    }
  } else {
    productCategory = 'Source'
  }

  membershipCopy.name = getMembershipName(membershipCopy.name)

  const batteryRelatedSubscriptions = [
    'Battery and Gigafactory',
    'Battery & Gigafactory',
    'Battery Database',
    'Battery Cell',
  ]

  if (
    productCategory === pricingName &&
    batteryRelatedSubscriptions.includes(membershipCopy.name)
  ) {
    membershipCopy.name = LiB
  }

  return { productCategory, updatedMembership: membershipCopy }
}

/**
 * Merges the provided products and services with the default subscriptions
 * @param productsAndServices - The current products and services mapping
 * @returns The merged products and services mapping
 */
function mergeProductsAndServices(
  productsAndServices: TProductsAndServices,
): TProductsAndServices {
  for (const [key, value] of Object.entries(SUBSCRIPTIONS)) {
    if (!productsAndServices[key]) {
      productsAndServices[key] = [...value] as TFormattedSubscription[]
    } else {
      ;(value as TFormattedSubscription[]).forEach((subscription) => {
        if (
          !productsAndServices[key].some(
            (sub) => sub.slug === subscription.slug,
          )
        ) {
          productsAndServices[key].push(subscription as TFormattedSubscription)
        }
      })
    }
  }
  return productsAndServices
}

/**
 * Creates the final response structure from the products and services mapping
 * @param productsAndServices - The products and services mapping
 * @returns The structured response object
 */
function createProductsResponse(
  productsAndServices: TProductsAndServices,
): TProductsResponse {
  return {
    [PRICE_ASSESSMENTS.name]: productsAndServices[PRICE_ASSESSMENTS.name],
    [FORECASTS.name]: productsAndServices[FORECASTS.name],
    [SOURCE.name]: productsAndServices[SOURCE.name],
    [MARKET_ASSESSMENTS.name]: productsAndServices[MARKET_ASSESSMENTS.name],
    [ESG.name]: productsAndServices[ESG.name],
  }
}
